import React, { useEffect, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Button, CardContent, CircularProgress, Grid, Typography } from '@material-ui/core';
import _ from 'lodash';
import { ISubscriber } from '../../api-client/autogenerated';
import DashboardBlock from './DashboardBlock';
import { CancelButton } from '../custom-components/CustomButtons';
import { updateSubscriber } from '../../models/api/subscribers';
import WorkingDaysSettingsFields from '../working-days/WorkingDaysSettingsFields';
import {
  draftFromWorkingDaySettings,
  getWorkingDaySettingsError,
  workingDaySettingsFromDraft,
} from '../../scripts/working-days';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    description: {
      marginBottom: theme.spacing(2),
    },
    actionsRow: {
      marginTop: theme.spacing(1),
    },
    saveButton: {
      marginLeft: theme.spacing(1),
    },
    error: {
      color: theme.palette.error.main,
      marginTop: theme.spacing(1),
    },
  }),
);

type Props = {
  subscriber: ISubscriber;
  refreshSubscriber: () => Promise<unknown> | void;
};

/**
 * Lets a subscriber admin choose whether document/RFI review periods are counted in calendar or
 * working days, which weekdays count, the received-after cutoff time, and the default project
 * time zone. New projects copy these settings at creation and can override them; projects created
 * before the settings existed follow them directly.
 */
export default function WorkingDaysPanel({ subscriber, refreshSubscriber }: Props) {
  const classes = useStyles();

  const [draft, setDraft] = useState(() => draftFromWorkingDaySettings(subscriber));
  const [saved, setSaved] = useState(draft);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const next = draftFromWorkingDaySettings(subscriber);
    setSaved(next);
    setDraft(next);
  }, [subscriber]);

  const isDirty = !_.isEqual(draft, saved);
  const validationError = getWorkingDaySettingsError(draft);

  const handleDiscard = () => {
    setDraft(saved);
    setError(null);
  };

  const handleSave = () => {
    setIsSaving(true);
    setError(null);
    updateSubscriber(subscriber.id, workingDaySettingsFromDraft(draft))
      .then(() => refreshSubscriber())
      .catch(() => setError('Could not save working day settings. Please try again.'))
      .finally(() => setIsSaving(false));
  };

  return (
    <DashboardBlock title="Working Days & Review Deadlines">
      <CardContent>
        <Typography variant="body2" className={classes.description}>
          Defaults for how review deadlines are calculated on this subscriber&apos;s projects. New
          projects start from these settings and can adjust them during project creation or later in
          Construction Management setup.
        </Typography>

        <WorkingDaysSettingsFields
          value={draft}
          onChange={setDraft}
          disabled={isSaving}
          timeZoneLabel="Default Project Time Zone"
        />

        <Grid container justify="flex-end" alignItems="center" className={classes.actionsRow}>
          <CancelButton onClick={handleDiscard} disabled={!isDirty || isSaving}>
            Discard changes
          </CancelButton>
          <Button
            variant="contained"
            color="primary"
            className={classes.saveButton}
            disabled={!isDirty || isSaving || !!validationError}
            onClick={handleSave}
          >
            {isSaving ? <CircularProgress size={20} /> : 'Save settings'}
          </Button>
        </Grid>

        {error && (
          <Typography variant="body2" className={classes.error}>
            {error}
          </Typography>
        )}
      </CardContent>
    </DashboardBlock>
  );
}
